import React from 'react';
import { Network, Server, Monitor, FileText, ArrowRight } from 'lucide-react';
import { services, partners, images } from '../data/mock';
import { Button } from './ui/button';

const iconMap = {
  Network: Network,
  Server: Server,
  Monitor: Monitor,
  FileText: FileText
};

const ServicesSection = () => {
  const scrollToContact = () => {
    document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="servicios" className="py-24 bg-slate-950 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-orange-500/10 border border-orange-500/20 rounded-full text-orange-400 text-sm font-medium mb-4">
            Lo que hacemos
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Nuestros Servicios
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Somos especialistas en Networking, Windows Server y Soporte a usuarios de escritorio
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-blue-500 mx-auto rounded-full mt-6" />
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="group relative bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-orange-500/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-orange-500/10"
              >
                {/* Icon */}
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mb-5 group-hover:from-orange-500/30 group-hover:to-blue-500/30 transition-all duration-300">
                  <IconComponent className="text-blue-400 group-hover:text-orange-400 transition-colors" size={28} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-orange-400 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {service.description}
                </p>

                {/* Hover indicator */}
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="text-orange-400" size={20} />
                </div>

                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden rounded-tr-2xl">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-orange-500/10 to-transparent rotate-45 translate-x-16 -translate-y-16 group-hover:from-orange-500/20 transition-colors" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Support contracts highlight */}
        <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 border border-slate-700 mb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-orange-500/5" />
          
          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Contratos de Soporte Personalizados
              </h3>
              <p className="text-slate-300 mb-6">
                Ofrecemos planes de soporte flexibles que se ajustan a las necesidades específicas de cada cliente. Desde pequeñas empresas hasta grandes corporaciones, tenemos la solución perfecta para usted.
              </p>
              <Button
                onClick={scrollToContact}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 transition-all duration-300 hover:scale-105 group"
              >
                Solicitar Información
                <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={18} />
              </Button>
            </div>
            <div className="relative">
              <img
                src={images.networking}
                alt="Infraestructura de red"
                className="rounded-xl shadow-2xl w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent rounded-xl" />
            </div>
          </div>
        </div>

        {/* Partners section */}
        <div className="text-center">
          <p className="text-slate-400 mb-8 text-lg">Socios de negocios certificados</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {/* HP Logo */}
            <div className="group relative">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-slate-600 hover:bg-slate-800 transition-all duration-300 w-[160px] h-[100px] flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_magneto-networking/artifacts/slvj2gvn_Hp_logo.png" 
                  alt="HP Logo"
                  className="max-h-[60px] max-w-[120px] w-auto h-auto object-contain"
                />
              </div>
              <p className="text-slate-500 text-sm mt-3 group-hover:text-slate-300 transition-colors">HP</p>
            </div>
            
            {/* Ubiquiti Logo */}
            <div className="group relative">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-slate-600 hover:bg-slate-800 transition-all duration-300 w-[160px] h-[100px] flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_magneto-networking/artifacts/4vf2yhgx_Ubiquiti_Logo.png" 
                  alt="Ubiquiti Logo"
                  className="max-h-[60px] max-w-[120px] w-auto h-auto object-contain"
                />
              </div>
              <p className="text-slate-500 text-sm mt-3 group-hover:text-slate-300 transition-colors">Ubiquiti</p>
            </div>
            
            {/* Microsoft Logo */}
            <div className="group relative">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-slate-600 hover:bg-slate-800 transition-all duration-300 w-[160px] h-[100px] flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_magneto-networking/artifacts/esu2dvz5_Microsoft_sp.png" 
                  alt="Microsoft Small Business Specialist"
                  className="max-h-[60px] max-w-[120px] w-auto h-auto object-contain"
                />
              </div>
              <p className="text-slate-500 text-sm mt-3 group-hover:text-slate-300 transition-colors">Microsoft</p>
            </div>
            
            {/* Kaspersky Logo */}
            <div className="group relative">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-slate-600 hover:bg-slate-800 transition-all duration-300 w-[160px] h-[100px] flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_magneto-networking/artifacts/p8ox57l0_Kaspersky.png" 
                  alt="Kaspersky Logo"
                  className="max-h-[60px] max-w-[120px] w-auto h-auto object-contain"
                />
              </div>
              <p className="text-slate-500 text-sm mt-3 group-hover:text-slate-300 transition-colors">Kaspersky</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
